import React, { useEffect, useState, useCallback } from 'react';
import { portfolioImages } from '../../data/portfolioImages';
import ScrollToTop from '../ScrollToTop';

function shuffleOnce(items) {
	const arr = [...items];
	for (let i = arr.length - 1; i > 0; i -= 1) {
		const j = Math.floor(Math.random() * (i + 1));
		[arr[i], arr[j]] = [arr[j], arr[i]];
	}
	return arr;
}

function Portfolio() {
	const [loadedImages, setLoadedImages] = useState(() => new Set());
	const [modalIndex, setModalIndex] = useState(null); // number | null
	const [shuffledImages] = useState(() => shuffleOnce(portfolioImages));

	const handleImageLoad = useCallback((imageId) => {
		setLoadedImages((prev) => {
			if (prev.has(imageId)) return prev;
			const next = new Set(prev);
			next.add(imageId);
			return next;
		});
	}, []);

	useEffect(() => {
		shuffledImages.forEach((img) => {
			const preload = new Image();
			preload.src = img.src;
		});
	}, [shuffledImages]);

	useEffect(() => {
		if (modalIndex === null) return;
		const onKey = (e) => {
			if (e.key === 'Escape') {
				setModalIndex(null);
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [modalIndex]);

	return (
		<>
			<div className="w-full">
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-0">
					{shuffledImages.map((img, index) => (
						<div key={img.id}>
							<div
								className="w-full pt-[100%] relative cursor-pointer overflow-hidden bg-white"
								onClick={() => setModalIndex(index)}
							>
								{!loadedImages.has(img.id) && (
									<div className="absolute inset-0 bg-gray-200 animate-pulse" />
								)}
								<img
									src={img.src}
									alt={img.title}
									className={`absolute top-0 left-0 w-full h-full object-contain hover:scale-105 transition-all duration-300 select-none ${
										loadedImages.has(img.id) ? 'opacity-100' : 'opacity-0'
									}`}
									onContextMenu={(e) => e.preventDefault()}
									onLoad={() => handleImageLoad(img.id)}
									onError={() => handleImageLoad(img.id)}
									draggable="false"
									loading="lazy"
									style={{
										WebkitTouchCallout: 'none',
										userSelect: 'none',
										WebkitUserSelect: 'none',
										pointerEvents: 'none',
									}}
								/>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Modal */}
			{modalIndex !== null && shuffledImages[modalIndex] && (
				<div
					className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
					onClick={() => setModalIndex(null)}
				>
					<div className="relative max-w-3xl max-h-[90vh] w-full mx-4 flex flex-col items-center">
						<img
							src={shuffledImages[modalIndex].src}
							alt={shuffledImages[modalIndex].title}
							className="max-h-[85vh] w-auto object-contain"
							loading="lazy"
							onClick={(e) => e.stopPropagation()}
						/>
						<button
							className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/75 transition-colors"
							onClick={(e) => {
								e.stopPropagation();
								setModalIndex(null);
							}}
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>
				</div>
			)}

			<ScrollToTop />
		</>
	);
}

export default Portfolio;
